import React, { useEffect, useContext, useState } from 'react'
import { useRouter } from 'next/router'
import { Box, Flex, Text, Button } from 'pcln-design-system'
import { isEmpty } from 'ramda'
import styled from 'styled-components'
import { Calendar } from '@pcln/date-picker'
import { useForm } from 'react-hook-form'
import { ANALYTICS_CATEGORIES } from '@/constants/analytics'
import analytics from '@/shared-utils/analytics'
import { getHotelDateRange } from '@/shared-utils/date-helper'
import SearchFormButton from '@/components/SearchFormButton'
import TypeAhead from '@/components/TypeAhead'
import { PopoverContainer } from '@/components/Popover'
import ShadowEffect from '@/components/ShadowEffect'
import CalendarWrapperBox from '@/components/CalenderWrapperBox.styled'
import DateField from '@/components/DateField'
import TravelerSelection from '@/components/TravelerSelection/TravelerSelection'
import { VP_DISCLAIMER } from '@/components/TravelerSelection/constants'
import BootstrapDataContext from '@/context/BootstrapDataContext'
import useTabGroup from '@/hooks/useTabGroup'
import { LOCATION_SEARCH_TYPE } from '@/types'
import { yupResolver } from '@hookform/resolvers/yup'
import { CALENDAR_POPOVER_SELECTOR } from '../../constants'
import {
  fields,
  getInitialState,
  setHotels,
  addDestination,
  removeDestination,
  handleMultiHotelSubmit,
  buildMultiHotelAnalyticsLabel,
  MutliHotelFormErrorsType
} from './utils'
import { searchFormSchema } from './validation'

import { HOTEL_FORM_STATE_TYPE } from '../../types'

import { MULTI_HOTEL_FORM_STATE_TYPE } from './types'

type MultiHotelFormType = {
  firstHotel?: Partial<HOTEL_FORM_STATE_TYPE> | null
}

const StyledText = styled(Text)`
  padding-top: 8px;
  padding-bottom: 8px;
`

const StyledButton = styled(Button)`
  padding: 6px 12px;
`

function MultiHotelForm({ firstHotel }: MultiHotelFormType) {
  const [isHotelCalendarOpen, setIsHotelCalendarOpen] = useState<boolean[]>([
    false,
    false,
    false,
    false,
    false
  ])
  const { registerTabElement } = useTabGroup()
  const { webstats, customer, isMobile } = useContext(BootstrapDataContext)

  const { pathname } = useRouter()
  const ANALYTICS_CATEGORY = ANALYTICS_CATEGORIES[pathname]
  const isHomepage = pathname === '/'

  const initialValues = getInitialState(firstHotel)

  const {
    clearErrors,
    register,
    unregister,
    setValue,
    handleSubmit,
    watch,
    trigger,
    formState: { errors }
  } = useForm<MULTI_HOTEL_FORM_STATE_TYPE>({
    defaultValues: initialValues,
    mode: 'onSubmit',
    resolver: yupResolver(searchFormSchema),
    reValidateMode: 'onChange'
  })

  useEffect(() => {
    fields.forEach(name => {
      register(name)
    })
    return () => {
      fields.forEach(name => {
        unregister(name)
      })
    }
  }, [register, unregister])

  const {
    hotels: watchHotels,
    roomCount: watchRoomCount,
    adultCount: watchAdultCount,
    childrenAges: watchChildrenAges,
    childrenCount: watchChildrenCount
  } = watch()

  // log errors to GA
  useEffect(() => {
    if (!isEmpty(errors)) {
      const label = buildMultiHotelAnalyticsLabel(
        errors as MutliHotelFormErrorsType
      )
      analytics.fireEvent({
        action: 'Multi-Hotel Search Button',
        category: ANALYTICS_CATEGORY,
        label
      })
    }
  }, [errors, ANALYTICS_CATEGORY])

  const maxDaysToSelect = 30

  const inputFormMargin = 3

  const searchBtnMargin = 1

  const typeaheadWidth = [1, 1, 1 / 2]

  const calendarWidth = [1, 1, 1 / 2]

  const roomSelectionWidth = [1, null, 1 / 2, null, 1 / 2]

  const searchButtonWidth = [1, null, 1 / 2, null, 1 / 2]

  // trigger children validation on change
  useEffect(() => {
    trigger('childrenCount')
  }, [watchChildrenCount, watchAdultCount, watchChildrenAges, trigger])

  return (
    <form
      data-testid="multi-hotel-form"
      aria-label="hotel-search-form"
      onSubmit={handleSubmit((data: MULTI_HOTEL_FORM_STATE_TYPE) => {
        analytics.fireEvent({
          action: 'hotel search button',
          category: isHomepage ? 'pcln_home' : 'hotel_home',
          label: 'multi-destination'
        })
        handleMultiHotelSubmit(data)
      })}
    >
      <Flex mx={-2} flexWrap="wrap">
        {watchHotels.map((hotel, index) => {
          return (
            <React.Fragment key={hotel.id}>
              <Flex
                width={1}
                mb={2}
                justifyContent="space-between"
                alignItems="center"
              >
                <StyledText pl={2} fontSize={14} bold>
                  Hotel {index + 1}
                </StyledText>
                {index > 1 && (
                  <StyledButton
                    color="primary"
                    mx={2}
                    mt={-2}
                    variation="subtle"
                    onClick={(e: Event) => {
                      e.preventDefault()
                      removeDestination(watchHotels, setValue, index)
                      clearErrors(`hotels.${index}`)
                    }}
                  >
                    Remove
                  </StyledButton>
                )}
              </Flex>
              <Flex direction="row" flexWrap="wrap" width={1}>
                <Box width={typeaheadWidth} mb={inputFormMargin} px={2}>
                  <ShadowEffect forProperty="location">
                    {({
                      disableShadowState
                    }: {
                      disableShadowState: () => void
                    }) => (
                      <Box textAlign="left">
                        <TypeAhead
                          searchProduct="packagesDestination"
                          data-testid="typeahead"
                          label="Where to?"
                          placeholder="Where to?"
                          searchKey={`hotels.${index}.endLocation`}
                          errors={errors}
                          disableShadowState={disableShadowState}
                          defaultSelectedItem={hotel.endLocation}
                          ref={(elem: HTMLInputElement) => {
                            registerTabElement(elem)
                          }}
                          showCurrentLocation={false}
                          onItemSelect={(item: LOCATION_SEARCH_TYPE) => {
                            setHotels(
                              watchHotels,
                              {
                                endLocation: item
                              },
                              setValue,
                              index
                            )
                            if (errors?.hotels?.[index]?.endLocation) {
                              trigger(`hotels.${index}.endLocation`)
                            }
                          }}
                        />
                      </Box>
                    )}
                  </ShadowEffect>
                </Box>
                <Box
                  width={calendarWidth}
                  mb={inputFormMargin}
                  px={2}
                  id="hotel-date-range-box"
                  textAlign="left"
                >
                  <ShadowEffect forProperty={`hotel-date-range-${hotel.id}`}>
                    {({
                      disableShadowState
                    }: {
                      disableShadowState: () => void
                    }) => (
                      <PopoverContainer
                        open={isHotelCalendarOpen[index]}
                        data-calendar
                        onDismiss={() => {
                          setIsHotelCalendarOpen(
                            isHotelCalendarOpen.map(() => false)
                          )
                          disableShadowState()
                        }}
                        domSelectorForVisuallyContainedElement={
                          CALENDAR_POPOVER_SELECTOR
                        }
                      >
                        <DateField
                          id={`hotel-date-range-${index}`}
                          data-testid={`hotel-date-range-${index}`}
                          m={0}
                          ref={registerTabElement}
                          aria-expanded={
                            isHotelCalendarOpen[index] ? 'true' : 'false'
                          }
                          error={
                            errors?.hotels?.[index]?.startDate?.message ||
                            errors?.hotels?.[index]?.endDate?.message
                          }
                          label="Check-in - Check-out"
                          name="hotelDates"
                          onFocus={() => {
                            setIsHotelCalendarOpen(
                              isHotelCalendarOpen.map((_, i) => index === i)
                            )
                          }}
                          value={getHotelDateRange({
                            startDate: hotel.startDate,
                            endDate: hotel.endDate
                          })}
                        />
                        <CalendarWrapperBox
                          boxShadowSize={!isMobile && 'overlay-lg'}
                          borderRadius={!isMobile && '2xl'}
                        >
                          <Calendar
                            roundedCorners
                            maxDaysToSelect={maxDaysToSelect}
                            monthWidthPx={350}
                            startDate={hotel.startDate}
                            endDate={hotel.endDate}
                            onChange={(
                              _: unknown,
                              {
                                startDate,
                                endDate
                              }: { startDate: string; endDate: string }
                            ) => {
                              if (startDate && endDate) {
                                setIsHotelCalendarOpen(
                                  isHotelCalendarOpen.map(() => false)
                                )
                                disableShadowState()
                              }
                              const dateObj = { startDate, endDate }
                              setHotels(watchHotels, dateObj, setValue, index)
                              if (startDate)
                                trigger(`hotels.${index}.startDate`)
                              if (endDate) trigger(`hotels.${index}.endDate`)
                            }}
                            monthsToShow={2}
                            onDismiss={() => {
                              setIsHotelCalendarOpen(
                                isHotelCalendarOpen.map(() => false)
                              )
                              disableShadowState()
                            }}
                            useDefaultFooter
                          />
                        </CalendarWrapperBox>
                      </PopoverContainer>
                    )}
                  </ShadowEffect>
                </Box>
              </Flex>
            </React.Fragment>
          )
        })}
        <Flex width={1} alignItems="center" mb={3}>
          <StyledButton
            color="primary"
            mx={2}
            p={3}
            variation="subtle"
            disabled={watchHotels.length > 4}
            onClick={(e: Event) => {
              e.preventDefault()
              addDestination(watchHotels, setValue)
            }}
          >
            + Add another hotel
          </StyledButton>
          <Text color="text.light" fontSize={12}>
            Add up to 5 hotels for your trip
          </Text>
        </Flex>
        <Box width={roomSelectionWidth} mb={3} px={2}>
          <ShadowEffect
            enabled
            forProperty="travelers-selection-readonly-input"
            alternativeBlurHandling
          >
            {({
              disableShadowState
            }: {
              // eslint-disable-next-line react/no-unused-prop-types
              disableShadowState: () => void
            }) => (
              <TravelerSelection
                adultCount={watchAdultCount}
                childrenAges={watchChildrenAges}
                childrenCount={watchChildrenCount}
                disableShadowState={disableShadowState}
                disclaimer={VP_DISCLAIMER}
                isMobile={false}
                error={
                  errors?.childrenCount?.message ||
                  errors?.roomCount?.message ||
                  ''
                }
                infantCount={0}
                onDoneButtonClick={disableShadowState}
                roomCount={watchRoomCount}
                setValue={setValue}
                showChildAgeSelectors
                showInfantMenuItem={false}
                showRoomMenuItem
                showSubLabel={false}
                showYouthMenuItem={false}
                youthCount={0}
              />
            )}
          </ShadowEffect>
        </Box>
        <Box
          width={searchButtonWidth}
          px={2}
          mb={searchBtnMargin}
          ml="center"
          style={{
            'overflow-y': 'visible',
            height: !isHomepage && '0px'
          }}
        >
          <SearchFormButton
            buttonText="Find Your Hotel"
            data-testid="HOTELS_SUBMIT_BUTTON"
            data-rudder-event
            data-sub-type="search"
            data-source={isHomepage ? 'homepage' : 'hotel landing'}
            data-parent-product="MULTI HOTEL"
            data-cguid={webstats.clientGUID}
            data-email-address={customer.emailAddress}
          />
        </Box>
      </Flex>
    </form>
  )
}

export default MultiHotelForm
